import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, map } from 'rxjs';
import {
  PipingFilterCategory,
  PipingObjectType,
  PipingSearchFilterModel,
} from 'src/app/shared/models/autogenerated-piping';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root',
})
export class DynamicPipingFiltersService {
  private readonly metadataEndpoint: string = `${environment.pipingApiEndpoint}/api/PdfPipingFilters/filtersByObjectType`;

  constructor(private readonly httpClient: HttpClient) { }

  /**
   * Loads PDF filter metadata by object type from the piping API.
   * @param objectType The current piping object type selection.
   * @param businessUnit Currently unused. Kept for signature compatibility with existing call sites.
   * @returns A list of filter models ready for the existing UI components.
   */
  public loadFilterMetadata(objectType: PipingObjectType, businessUnit?: string): Observable<PipingSearchFilterModel[]> {
    void businessUnit;
    const url: string = `${this.metadataEndpoint}/${this.toApiObjectType(objectType)}`;

    return this.httpClient.get<unknown>(url).pipe(
      map((response: unknown) => this.toPipingSearchFilters(response, objectType))
    );
  }

  private toApiObjectType(objectType: PipingObjectType): string {
    if (objectType === PipingObjectType._1) {
      return 'PIPE';
    }

    if (objectType === PipingObjectType._2) {
      return 'VALVE';
    }

    return 'DEFAULT';
  }

  private toPipingSearchFilters(response: unknown, objectType: PipingObjectType): PipingSearchFilterModel[] {
    const rawArray: unknown[] = this.toUnknownArray(response);
    const filterCategory: PipingFilterCategory = this.toFilterCategory(objectType);

    return rawArray
      .map((item: unknown, index: number) => {
        const rawItem: Record<string, unknown> = this.toRecord(item);
        const mappingField: string = String(
          rawItem['filterPropertyName'] ?? rawItem['mappingField'] ?? rawItem['field'] ?? ''
        );

        if (!mappingField) {
          return undefined;
        }

        const filterModel: PipingSearchFilterModel = new PipingSearchFilterModel();
        filterModel.filterPropertyName = mappingField;
        filterModel.filterDisplayName = String(
          rawItem['filterDisplayName'] ?? rawItem['displayName'] ?? mappingField
        );
        filterModel.filterType = String(rawItem['filterType'] ?? 'MULTI_SELECT');
        filterModel.displayOrder = Number(rawItem['displayOrder'] ?? index);
        filterModel.options = this.toStringArray(rawItem['options'] ?? rawItem['values']);
        filterModel.filterCategory = this.toKnownFilterCategory(rawItem['filterCategory'], filterCategory);

        return filterModel;
      })
      .filter((item: PipingSearchFilterModel | undefined): item is PipingSearchFilterModel => item !== undefined)
      .sort((left: PipingSearchFilterModel, right: PipingSearchFilterModel) => (left.displayOrder ?? 0) - (right.displayOrder ?? 0));
  }

  private toKnownFilterCategory(value: unknown, fallback: PipingFilterCategory): PipingFilterCategory {
    const categoryValue: number = Number(value);
    if (
      categoryValue === PipingFilterCategory._0
      || categoryValue === PipingFilterCategory._1
      || categoryValue === PipingFilterCategory._2
      || categoryValue === PipingFilterCategory._3
    ) {
      return categoryValue as PipingFilterCategory;
    }

    return fallback;
  }

  private toFilterCategory(objectType: PipingObjectType): PipingFilterCategory {
    if (objectType === PipingObjectType._1) {
      return PipingFilterCategory._1;
    }

    if (objectType === PipingObjectType._2) {
      return PipingFilterCategory._2;
    }

    return PipingFilterCategory._0;
  }

  private toUnknownArray(value: unknown): unknown[] {
    if (Array.isArray(value)) {
      return value;
    }

    const rawRecord: Record<string, unknown> = this.toRecord(value);
    if (Array.isArray(rawRecord['data'])) {
      return rawRecord['data'] as unknown[];
    }

    if (Array.isArray(rawRecord['items'])) {
      return rawRecord['items'] as unknown[];
    }

    return [];
  }

  private toStringArray(value: unknown): string[] {
    const rawArray: unknown[] = this.toUnknownArray(value);
    return rawArray
      .map((item: unknown) => String(item ?? ''))
      .filter((item: string) => item.length > 0);
  }

  private toRecord(value: unknown): Record<string, unknown> {
    if (value && typeof value === 'object' && !Array.isArray(value)) {
      return value as Record<string, unknown>;
    }

    return {};
  }
}
